/** Minimal server-sent-events reader over a fetch Response.
 *
 * Shared by the dialer's two SSE consumers. Yields one item per `event:`/`data:`
 * block; a block with no data line is skipped, and unparseable JSON is handed
 * back as `{ raw }` rather than throwing, so one malformed frame can't kill the
 * stream.
 */
export async function* runSseStream(
  response: Response,
): AsyncGenerator<{ type: string; payload: any }> {
  if (!response.body) throw new Error('Streaming response body is not available')
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  const dispatch = (raw: string) => {
    const lines = raw.split('\n')
    let eventType = 'message'
    const dataLines: string[] = []
    for (const line of lines) {
      if (line.startsWith('event:')) eventType = line.slice(6).trim() || 'message'
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
    }
    if (!dataLines.length) return null
    let payload: any = {}
    try {
      payload = JSON.parse(dataLines.join('\n'))
    } catch {
      payload = { raw: dataLines.join('\n') }
    }
    return { type: eventType, payload }
  }
  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const blocks = buffer.split('\n\n')
    buffer = blocks.pop() || ''
    for (const block of blocks) {
      const item = dispatch(block)
      if (item) yield item
    }
  }
  const tail = dispatch(buffer)
  if (tail) yield tail
}
